import {batch, useSelector} from "react-redux";

import {getFavouriteObject} from "@pg-mono/favourites";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {openModal} from "../../real_estate/state/subscribe_real_estate_modal_slice";
import {useAppDispatch} from "../../store/hooks";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {addToComparison, addToFavourites, removeFromComparison, removeFromFavourites} from "../actions/manage_favourites";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement, IAmplitudeFavouriteListContext} from "../tracking/get_amplitude_favourites_event_properties";
import {
    addRealEstateToFavouritesSuccess,
    addToComparisonError,
    removeFromComparisonError,
    removeFromComparisonSuccess,
    removeRealEstateFromFavouritesSuccess
} from "../utils/alerts";
import {
    removePendingComparisonPropertyId,
    setPendingComparisonPropertyId,
    setPendingFavouriteOfferId,
    setPendingFavouritePropertyId
} from "../utils/pending_favourites_auth";
import {useFavoritesAuth} from "./use_favorites_auth";
import {useFavouritesTopbarExperiment} from "./use_favourites_topbar_experiment";

export const useFavourites = () => {
    const dispatch = useAppDispatch();
    const favoritesAuth = useFavoritesAuth();
    const {isMobile} = useUserDevice();
    const {isFavouritesTopbarTreatment} = useFavouritesTopbarExperiment();

    const favouriteOffers = useSelector((state: IRPStore) => state.favourite.favourites.offers);
    const favouriteProperties = useSelector((state: IRPStore) => state.favourite.favourites.properties);

    const favourites = useSelector((state: IRPStore) => state.favourite.favourites);
    const fetchRequestState = useSelector((state: IRPStore) => state.favourite.fetchRequest);
    const isAuthenticated = useIsAuthenticated();
    const isTreatmentGuest = isFavouritesTopbarTreatment && !isAuthenticated;
    const persistToLocalStorage = !isTreatmentGuest;

    //  In case user isn't authenticated, favourites are taken from localStorage
    const isLoaded = isAuthenticated ? fetchRequestState === RequestState.Success || fetchRequestState === RequestState.Error : favourites.initialized;

    return {
        favouritePropertiesIds: favouriteProperties,
        favouriteOfferIds: favouriteOffers,
        loaded: isLoaded,
        isTreatmentGuest,
        fetchRequest: fetchRequestState,
        isOfferInFavourites: (offerId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favouriteOffers.includes(offerId);
        },
        isPropertyInFavourites: (propertyId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favouriteProperties.includes(propertyId);
        },
        isPropertyInComparison: (propertyId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favourites.list.some((favourite) => favourite.property?.rp_id === propertyId && favourite.in_comparison);
        },
        addPropertyToFavourites: async (
            propertyId: number,
            offerId: number,
            priceAlert?: boolean,
            amplitudePlacement?: AmplitudeFavouritePlacement,
            favouriteLocation?: AmplitudeFavouriteLocation,
            listContext?: IAmplitudeFavouriteListContext
        ) => {
            const favouriteObject = getFavouriteObject({propertyId});
            const favourite = {...favouriteObject, price_alerts: !!priceAlert};

            const result = await dispatch(addToFavourites([favourite], true, persistToLocalStorage));

            if (result instanceof Error) {
                return false;
            }

            batch(() => {
                if (isAuthenticated) {
                    dispatch(openModal());
                }
            });

            if (!isAuthenticated) {
                if (isTreatmentGuest) {
                    setPendingFavouritePropertyId(propertyId, {
                        offerId,
                        placement: amplitudePlacement,
                        location: favouriteLocation,
                        listContext
                    });
                }
                favoritesAuth({
                    type: "property",
                    amplitudePlacement
                });
            }

            if (isAuthenticated || (!isFavouritesTopbarTreatment && !isMobile)) {
                dispatch(addRealEstateToFavouritesSuccess);
            }

            return true;
        },
        addPropertyToComparison: async (propertyId: number) => {
            const isAlreadyInComparison = favourites.list.some((favourite) => favourite.property?.rp_id === propertyId && favourite.in_comparison);

            if (isAlreadyInComparison) {
                return true;
            }

            const existingFavourite = favourites.list.find((favourite) => favourite.property?.rp_id === propertyId);
            const favourite = {
                ...getFavouriteObject({propertyId}),
                ...existingFavourite,
                in_comparison: true
            };

            const result = await dispatch(addToComparison(favourite, persistToLocalStorage));

            if (!result) {
                dispatch(addToComparisonError);
                return false;
            }

            if (!isTreatmentGuest) {
                hitGtmAddToCompareClick(true);
            }

            if (isTreatmentGuest) {
                if (!existingFavourite) {
                    setPendingFavouritePropertyId(propertyId, {
                        placement: "rp_favourite_property_compare",
                        location: "property_page"
                    });
                }
                setPendingComparisonPropertyId(propertyId);
                favoritesAuth({
                    type: "property",
                    amplitudePlacement: "rp_favourite_property_compare"
                });
            }

            return true;
        },
        removePropertyFromComparison: async (propertyId: number) => {
            const existingFavourite = favourites.list.find((favourite) => favourite.property?.rp_id === propertyId && favourite.in_comparison);

            if (!existingFavourite) {
                return true;
            }

            const result = await dispatch(removeFromComparison({...existingFavourite, in_comparison: false}, persistToLocalStorage));

            if (!result) {
                dispatch(removeFromComparisonError);
                return false;
            }

            if (!isTreatmentGuest) {
                hitGtmAddToCompareClick(false);
            }

            if (isTreatmentGuest) {
                removePendingComparisonPropertyId(propertyId);
            }

            dispatch(removeFromComparisonSuccess);

            return true;
        },
        addPropertyToFavouritesWithoutAuthCheck: async (propertyId: number, _offerId: number, priceAlert?: boolean, hideNotification: boolean = false) => {
            const favouriteObject = getFavouriteObject({propertyId});
            const favourite = {...favouriteObject, price_alerts: !!priceAlert};

            batch(() => {
                dispatch(addToFavourites([favourite]));
                !hideNotification && dispatch(addRealEstateToFavouritesSuccess);
            });
        },
        removePropertyFromFavourites: async (propertyId: number) => {
            const favourite = getFavouriteObject({propertyId});

            const result = await dispatch(removeFromFavourites([favourite]));

            if (result instanceof Error) {
                return false;
            }

            dispatch(removeRealEstateFromFavouritesSuccess);

            return true;
        },
        addOfferToFavourites: async (offerId: number, amplitudePlacement?: AmplitudeFavouritePlacement, favouriteLocation?: AmplitudeFavouriteLocation) => {
            const favourite = getFavouriteObject({offerId});

            const result = await dispatch(addToFavourites([favourite], true, persistToLocalStorage));

            if (result instanceof Error) {
                return false;
            }

            batch(() => {
                if (isAuthenticated || !isFavouritesTopbarTreatment) {
                    dispatch(addRealEstateToFavouritesSuccess);
                }
                if (isAuthenticated) {
                    dispatch(openModal());
                }
            });

            if (!isAuthenticated) {
                if (isTreatmentGuest) {
                    setPendingFavouriteOfferId(offerId, {
                        placement: amplitudePlacement,
                        location: favouriteLocation
                    });
                }
                favoritesAuth({
                    type: "offer",
                    amplitudePlacement
                });
            }

            return true;
        },
        addOfferToFavouritesWithoutAuthCheck: async (offerId: number, hideNotification: boolean = false) => {
            const favourite = getFavouriteObject({offerId});

            // Always add to favorites first (for unauthenticated users, this adds to localStorage)
            batch(() => {
                dispatch(addToFavourites([favourite]));
                !hideNotification && dispatch(addRealEstateToFavouritesSuccess);
            });
        },
        removeOfferFromFavourites: async (offerId: number) => {
            const favourite = getFavouriteObject({offerId});

            const result = await dispatch(removeFromFavourites([favourite]));

            if (result instanceof Error) {
                return false;
            }

            dispatch(removeRealEstateFromFavouritesSuccess);

            return true;
        }
    };
};
import {batch, useSelector} from "react-redux";

import {getFavouriteObject} from "@pg-mono/favourites";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {openModal} from "../../real_estate/state/subscribe_real_estate_modal_slice";
import {useAppDispatch} from "../../store/hooks";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {addToComparison, addToFavourites, removeFromComparison, removeFromFavourites} from "../actions/manage_favourites";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement, IAmplitudeFavouriteListContext} from "../tracking/get_amplitude_favourites_event_properties";
import {
    addRealEstateToFavouritesSuccess,
    addToComparisonError,
    removeFromComparisonError,
    removeFromComparisonSuccess,
    removeRealEstateFromFavouritesSuccess
} from "../utils/alerts";
import {
    removePendingComparisonPropertyId,
    setPendingComparisonPropertyId,
    setPendingFavouriteOfferId,
    setPendingFavouritePropertyId
} from "../utils/pending_favourites_auth";
import {useFavoritesAuth} from "./use_favorites_auth";
import {useFavouritesTopbarExperiment} from "./use_favourites_topbar_experiment";

export const useFavourites = () => {
    const dispatch = useAppDispatch();
    const favoritesAuth = useFavoritesAuth();
    const {isMobile} = useUserDevice();
    const {isFavouritesTopbarTreatment} = useFavouritesTopbarExperiment();

    const favouriteOffers = useSelector((state: IRPStore) => state.favourite.favourites.offers);
    const favouriteProperties = useSelector((state: IRPStore) => state.favourite.favourites.properties);

    const favourites = useSelector((state: IRPStore) => state.favourite.favourites);
    const fetchRequestState = useSelector((state: IRPStore) => state.favourite.fetchRequest);
    const isAuthenticated = useIsAuthenticated();
    const isTreatmentGuest = isFavouritesTopbarTreatment && !isAuthenticated;
    const persistToLocalStorage = !isTreatmentGuest;

    //  In case user isn't authenticated, favourites are taken from localStorage
    const isLoaded = isAuthenticated ? fetchRequestState === RequestState.Success || fetchRequestState === RequestState.Error : favourites.initialized;

    return {
        favouritePropertiesIds: favouriteProperties,
        favouriteOfferIds: favouriteOffers,
        loaded: isLoaded,
        isTreatmentGuest,
        fetchRequest: fetchRequestState,
        isOfferInFavourites: (offerId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favouriteOffers.includes(offerId);
        },
        isPropertyInFavourites: (propertyId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favouriteProperties.includes(propertyId);
        },
        isPropertyInComparison: (propertyId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favourites.list.some((favourite) => favourite.property?.rp_id === propertyId && favourite.in_comparison);
        },
        addPropertyToFavourites: async (
            propertyId: number,
            offerId: number,
            priceAlert?: boolean,
            amplitudePlacement?: AmplitudeFavouritePlacement,
            favouriteLocation?: AmplitudeFavouriteLocation,
            listContext?: IAmplitudeFavouriteListContext
        ) => {
            const favouriteObject = getFavouriteObject({propertyId});
            const favourite = {...favouriteObject, price_alerts: !!priceAlert};

            const result = await dispatch(addToFavourites([favourite], true, persistToLocalStorage));

            if (result instanceof Error) {
                return false;
            }

            batch(() => {
                if (isAuthenticated) {
                    dispatch(openModal());
                }
            });

            if (!isAuthenticated) {
                if (isTreatmentGuest) {
                    setPendingFavouritePropertyId(propertyId, {
                        offerId,
                        placement: amplitudePlacement,
                        location: favouriteLocation,
                        listContext
                    });
                }
                favoritesAuth({
                    type: "property",
                    amplitudePlacement
                });
            }

            if (isAuthenticated || (!isFavouritesTopbarTreatment && !isMobile)) {
                dispatch(addRealEstateToFavouritesSuccess);
            }

            return true;
        },
        addPropertyToComparison: async (propertyId: number) => {
            const isAlreadyInComparison = favourites.list.some((favourite) => favourite.property?.rp_id === propertyId && favourite.in_comparison);

            if (isAlreadyInComparison) {
                return true;
            }

            const existingFavourite = favourites.list.find((favourite) => favourite.property?.rp_id === propertyId);
            const favourite = {
                ...getFavouriteObject({propertyId}),
                ...existingFavourite,
                in_comparison: true
            };

            const result = await dispatch(addToComparison(favourite, persistToLocalStorage));

            if (!result) {
                dispatch(addToComparisonError);
                return false;
            }

            if (!isTreatmentGuest) {
                hitGtmAddToCompareClick(true);
            }

            if (isTreatmentGuest) {
                if (!existingFavourite) {
                    setPendingFavouritePropertyId(propertyId, {
                        placement: "rp_favourite_property_compare",
                        location: "property_page"
                    });
                }
                setPendingComparisonPropertyId(propertyId);
                favoritesAuth({
                    type: "property",
                    amplitudePlacement: "rp_favourite_property_compare"
                });
            }

            return true;
        },
        removePropertyFromComparison: async (propertyId: number) => {
            const existingFavourite = favourites.list.find((favourite) => favourite.property?.rp_id === propertyId && favourite.in_comparison);

            if (!existingFavourite) {
                return true;
            }

            const result = await dispatch(removeFromComparison({...existingFavourite, in_comparison: false}, persistToLocalStorage));

            if (!result) {
                dispatch(removeFromComparisonError);
                return false;
            }

            if (!isTreatmentGuest) {
                hitGtmAddToCompareClick(false);
            }

            if (isTreatmentGuest) {
                removePendingComparisonPropertyId(propertyId);
            }

            dispatch(removeFromComparisonSuccess);

            return true;
        },
        addPropertyToFavouritesWithoutAuthCheck: async (propertyId: number, _offerId: number, priceAlert?: boolean, hideNotification: boolean = false) => {
            const favouriteObject = getFavouriteObject({propertyId});
            const favourite = {...favouriteObject, price_alerts: !!priceAlert};

            batch(() => {
                dispatch(addToFavourites([favourite]));
                !hideNotification && dispatch(addRealEstateToFavouritesSuccess);
            });
        },
        removePropertyFromFavourites: async (propertyId: number) => {
            const favourite = getFavouriteObject({propertyId});

            const result = await dispatch(removeFromFavourites([favourite]));

            if (result instanceof Error) {
                return false;
            }

            dispatch(removeRealEstateFromFavouritesSuccess);

            return true;
        },
        addOfferToFavourites: async (offerId: number, amplitudePlacement?: AmplitudeFavouritePlacement, favouriteLocation?: AmplitudeFavouriteLocation) => {
            const favourite = getFavouriteObject({offerId});

            const result = await dispatch(addToFavourites([favourite], true, persistToLocalStorage));

            if (result instanceof Error) {
                return false;
            }

            batch(() => {
                if (isAuthenticated || !isFavouritesTopbarTreatment) {
                    dispatch(addRealEstateToFavouritesSuccess);
                }
                if (isAuthenticated) {
                    dispatch(openModal());
                }
            });

            if (!isAuthenticated) {
                if (isTreatmentGuest) {
                    setPendingFavouriteOfferId(offerId, {
                        placement: amplitudePlacement,
                        location: favouriteLocation
                    });
                }
                favoritesAuth({
                    type: "offer",
                    amplitudePlacement
                });
            }

            return true;
        },
        addOfferToFavouritesWithoutAuthCheck: async (offerId: number, hideNotification: boolean = false) => {
            const favourite = getFavouriteObject({offerId});

            // Always add to favorites first (for unauthenticated users, this adds to localStorage)
            batch(() => {
                dispatch(addToFavourites([favourite]));
                !hideNotification && dispatch(addRealEstateToFavouritesSuccess);
            });
        },
        removeOfferFromFavourites: async (offerId: number) => {
            const favourite = getFavouriteObject({offerId});

            const result = await dispatch(removeFromFavourites([favourite]));

            if (result instanceof Error) {
                return false;
            }

            dispatch(removeRealEstateFromFavouritesSuccess);

            return true;
        }
    };
};
